/**
 * usePdfExportSession — the non-visual plumbing both PDF export pages share:
 *
 *   - layout options seeded from the therapist's remembered toggles
 *     (`therapists/{uid}.treatmentPdfPrefs`) and remembered again after each
 *     successful export (toggles only, never the free texts);
 *   - saved-signature handling (SignaturePadModal → saveTherapistSignature →
 *     authSlice.setTherapistProfile);
 *   - the exported PDF url for PdfPreviewModal.
 *
 * Error codes are passed in so each page keeps its own CK range.
 */

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTherapistId,
  selectTherapistProfile,
  setTherapistProfile,
} from "../../store/slices/authSlice";
import { saveTherapistSignature, saveTreatmentPdfPrefs } from "../../firebase";
import { mergePrefs, normalizeOptions, pickPersistablePrefs } from "./pdfExportOptions";

export function usePdfExportSession({ signatureErrorCode, prefsErrorCode }) {
  const dispatch = useDispatch();
  const therapistId = useSelector(selectTherapistId);
  const therapistProfile = useSelector(selectTherapistProfile);
  const savedSignatureUrl = therapistProfile?.signature?.dataUrl || null;

  // ─── Options: defaults ← remembered prefs; "box" when nothing saved yet ───
  const [options, setOptions] = useState(() => mergePrefs(therapistProfile?.treatmentPdfPrefs));
  const [prefsSeeded, setPrefsSeeded] = useState(false);
  useEffect(() => {
    if (prefsSeeded || !therapistProfile) return;
    const seeded = mergePrefs(therapistProfile.treatmentPdfPrefs);
    if (!therapistProfile.treatmentPdfPrefs && !therapistProfile.signature?.dataUrl) {
      seeded.signature = "box";
    }
    setOptions(seeded);
    setPrefsSeeded(true);
  }, [therapistProfile, prefsSeeded]);

  const setOption = useCallback((group, key, value) => {
    setOptions((prev) =>
      group ? { ...prev, [group]: { ...prev[group], [key]: value } } : { ...prev, [key]: value }
    );
  }, []);
  const toggleOption = useCallback((group, key) => {
    setOptions((prev) => ({ ...prev, [group]: { ...prev[group], [key]: !prev[group][key] } }));
  }, []);
  const setText = useCallback((key, value) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const rememberPrefs = useCallback(
    async (opts) => {
      try {
        const prefs = pickPersistablePrefs(normalizeOptions(opts));
        await saveTreatmentPdfPrefs(therapistId, prefs);
        if (therapistProfile) {
          dispatch(setTherapistProfile({ ...therapistProfile, treatmentPdfPrefs: prefs }));
        }
      } catch (error) {
        // Silent — the PDF already exists; rules may not include the field yet.
        console.error(`[${prefsErrorCode}] Error remembering PDF layout prefs:`, {
          errorCode: prefsErrorCode,
          errorMessage: error?.message,
          timestamp: new Date().toISOString(),
        });
      }
    },
    [therapistId, therapistProfile, dispatch, prefsErrorCode]
  );

  // ─── Signature ───
  const [padOpen, setPadOpen] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState(null);

  const saveSignature = useCallback(
    async (dataUrl) => {
      setIsSavingSignature(true);
      setSignatureError(null);
      try {
        await saveTherapistSignature(therapistId, dataUrl);
        if (therapistProfile) {
          dispatch(
            setTherapistProfile({
              ...therapistProfile,
              signature: { dataUrl, updatedAt: new Date().toISOString() },
            })
          );
        }
        return true;
      } catch (error) {
        console.error(`[${signatureErrorCode}] Error saving therapist signature:`, {
          errorCode: signatureErrorCode,
          errorMessage: error?.message,
          timestamp: new Date().toISOString(),
        });
        setSignatureError("לא הצלחנו לשמור את החתימה. נסו שוב.");
        return false;
      } finally {
        setIsSavingSignature(false);
      }
    },
    [therapistId, therapistProfile, dispatch, signatureErrorCode]
  );

  // ─── Exported PDF ───
  const [pdfUrl, setPdfUrl] = useState(null);

  return {
    therapistId,
    therapistProfile,
    savedSignatureUrl,
    options,
    setOptions,
    setOption,
    toggleOption,
    setText,
    rememberPrefs,
    padOpen,
    openPad: () => setPadOpen(true),
    closePad: () => setPadOpen(false),
    saveSignature,
    isSavingSignature,
    signatureError,
    setSignatureError,
    pdfUrl,
    setPdfUrl,
  };
}

export default usePdfExportSession;
